import { useState, useEffect } from 'react';

export type KpiItem = {
  id: string;
  title: string;
  value: string;
  unit: string;
  trend: string;
  isPositive: boolean | null;
  icon: React.ReactNode;
  colorClass: string;
  isVisible: boolean;
};

const STORAGE_KEY = 'topKpis-layout';

export const useKpiLayout = (initialKpis: KpiItem[]) => {
  const [kpis, setKpis] = useState<KpiItem[]>([]);
  const [editMode, setEditMode] = useState(false);

  // Load saved layout from localStorage on initial load
  useEffect(() => {
    try {
      const savedLayout = localStorage.getItem(STORAGE_KEY);
      if (savedLayout) {
        // Get saved IDs and visibility states
        const savedState = JSON.parse(savedLayout);
        
        // If saved state exists, reorder and update visibility based on it
        if (savedState && savedState.kpiOrder && Array.isArray(savedState.kpiOrder)) {
          const kpiOrder = savedState.kpiOrder;
          const kpiVisibility = savedState.kpiVisibility || {};
          
          // Create a new array based on saved order
          const orderedKpis = [...initialKpis];
          
          // Sort the KPIs based on the saved order
          orderedKpis.sort((a, b) => {
            const indexA = kpiOrder.indexOf(a.id);
            const indexB = kpiOrder.indexOf(b.id);
            
            // If both exist in saved order, use that order
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            
            // If only one exists, prioritize the one in the saved order
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            
            // For new items not in saved order, keep original order
            return 0;
          });
          
          // Apply saved visibility state
          const updatedKpis = orderedKpis.map(kpi => ({
            ...kpi,
            isVisible: kpiVisibility[kpi.id] !== undefined ? kpiVisibility[kpi.id] : true
          }));
          
          setKpis(updatedKpis);
          return;
        }
      }
      
      // If no saved layout or invalid format, use initial kpis
      setKpis(initialKpis.map(kpi => ({ ...kpi, isVisible: true })));
    } catch (error) {
      console.error("Error loading saved layout:", error);
      setKpis(initialKpis.map(kpi => ({ ...kpi, isVisible: true })));
    }
  }, [initialKpis]);

  // Save layout to localStorage whenever it changes
  useEffect(() => {
    if (kpis.length > 0) {
      try {
        // Save the order of KPI IDs and visibility state
        const kpiOrder = kpis.map(kpi => kpi.id);
        const kpiVisibility = kpis.reduce((acc, kpi) => {
          acc[kpi.id] = kpi.isVisible;
          return acc;
        }, {} as Record<string, boolean>);
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          kpiOrder,
          kpiVisibility
        }));
      } catch (error) {
        console.error("Error saving layout:", error);
      }
    }
  }, [kpis]);

  const resetLayout = () => {
    setKpis(initialKpis.map(kpi => ({ ...kpi, isVisible: true })));
    localStorage.removeItem(STORAGE_KEY);
  };

  const toggleVisibility = (id: string) => {
    setKpis(prev => prev.map(kpi => 
      kpi.id === id ? { ...kpi, isVisible: !kpi.isVisible } : kpi
    ));
  };

  const toggleEditMode = () => {
    setEditMode(prev => !prev);
  };

  return {
    kpis,
    visibleKpis: kpis.filter(kpi => kpi.isVisible),
    hiddenKpis: kpis.filter(kpi => !kpi.isVisible),
    resetLayout,
    toggleVisibility,
    editMode,
    toggleEditMode
  };
};
